import React from "react";
import PropTypes from "prop-types";
import { useTooltip, TooltipPopup } from "@reach/tooltip";
import Portal from "@reach/portal";

// taken from https://github.com/reach/reach-ui/blob/master/packages/tooltip/examples/triangle.example.js
// Center the tooltip, but collisions will win
const centered = (triggerRect, tooltipRect) => {
  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  const left = triggerCenter - tooltipRect.width / 2;
  const maxLeft = window.innerWidth - tooltipRect.width - 2;
  return {
    left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
    top: triggerRect.bottom + 8 + window.scrollY,
  };
};

// eslint-disable-next-line react/prop-types
const TriangleTooltip = ({ children, label, ariaLabel, contentWidth, contentPadding, contentSize, disableArrow }) => {
  // get the props from useTooltip
  const [trigger, tooltip] = useTooltip();

  // destructure off what we need to position the triangle
  const { isVisible, triggerRect } = tooltip;
  const extra_props = disableArrow ? {} : { position: centered };
  const children_trigger = typeof children === "string" ? <span>{children}</span> : children;

  return (
    <React.Fragment>
      {React.cloneElement(children_trigger, trigger)}

      {isVisible && !disableArrow && (
        // The Triangle. We position it relative to the trigger, not the popup
        // so that collisions don't have a triangle pointing off to nowhere.
        // Using a Portal may seem a little extreme, but we can keep the
        // positioning logic simpler here instead of needing to consider
        // the popup's position relative to the trigger and collisions
        <Portal>
          <div
            className="et--tooltip2--arrow"
            style={{
              left: triggerRect && triggerRect.left - 10 + triggerRect.width / 2,
              top: triggerRect && triggerRect.bottom + window.scrollY,
            }}
          />
        </Portal>
      )}
      <TooltipPopup
        {...tooltip}
        label={label}
        ariaLabel={ariaLabel}
        className="et--tooltip2"
        style={{
          padding: contentPadding,
          width: contentWidth,
          fontSize: contentSize,
        }}
        {...extra_props}
      />
    </React.Fragment>
  );
};

const Tooltip2 = ({
  contentWidth,
  contentPadding = "15px",
  contentSize = "14px",
  trigger,
  children,
  disableArrow,
  ...rest
}) => {
  return (
    <TriangleTooltip
      label={children}
      contentWidth={contentWidth}
      contentPadding={contentPadding}
      contentSize={contentSize}
      disableArrow={disableArrow}
      {...rest}
    >
      {trigger}
    </TriangleTooltip>
  );
};

Tooltip2.propTypes = {
  contentWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  contentPadding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  contentSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  trigger: PropTypes.any,
  children: PropTypes.any,
  disableArrow: PropTypes.bool,
};

export default Tooltip2;
